import { LoadingTypes, isLoading as Loading } from '@labrav/common'
import {
  ButtonV2,
  LoadingLogo,
  MainTabsV2,
  ModalConfirmV2,
  ModalSizeVariants,
} from '@labrav/react-components'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useStyles } from './AddEditKeywords.styles'
import awsImage from '../../../common/images/awsCloud.png'
import gcpImage from '../../../common/images/gcpMarketPlace.png'
import azureImage from '../../../common/images/azureCloud.png'
import { AddEditCompetitors } from '../AddEditCompetitors/AddEditCompetitors'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRankingStar } from '@fortawesome/pro-regular-svg-icons'
import { FieldArray, Form, Formik } from 'formik'
import { useDispatch, useSelector } from '../../../store'
import {
  KeywordCompetitorMapping,
  KeywordType,
  CompetitorType,
} from '../../../modules/mpsSeo/reducer'
import {
  getKeywordCompetitorMapping,
  searchRanking,
} from '../../../modules/mpsSeo/action'
import KeywordCompetitorValidation from '../KeywordCompetitorValidation'
import { AddEditKeywordsList } from './AddEditKeywordsList'
import { SEO_CLOUD_MARKETPLACE, useSeoAccountId } from '../seoCloud'

export type KeyWordMap = {
  id?: string
  keywords: KeywordType[]
  competitors: CompetitorType[]
}

export type KeywordCompetitorForm = { keywordCompetitorMapping: KeyWordMap[] }

export const AddEditKeywords = ({
  openKeyword,
  setOpenKeyword,
  title,
}: {
  openKeyword: boolean
  setOpenKeyword: Dispatch<SetStateAction<boolean>>
  title?: string
}) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const selectedCloud = SEO_CLOUD_MARKETPLACE
  const accountId = useSeoAccountId()
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [openCompetitor, setOpenCompetitor] = useState(false)

  const keywordCompetitorList = useSelector<KeywordCompetitorMapping[]>(
    state => state.marketplaceSeo[selectedCloud].keywordCompetitorMapping
  )

  const isLoadingKeywordCompetitor = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.FETCH_KEYWORD_COMPETITOR_MAPPING)
  )

  useEffect(() => {
    if (accountId && selectedCloud) {
      dispatch(getKeywordCompetitorMapping(accountId, selectedCloud))
    }
  }, [accountId, selectedCloud])

  const cloudTabs = [
    {
      label: 'AWS',
      key: 'aws',
      icon: <img src={awsImage} alt="aws-image" className={classes.awsImage} />,
      renderElement: () => null,
    },
    {
      label: 'GCP',
      key: 'gcp',
      icon: (
        <img src={gcpImage} alt="gcp-image" className={classes.cloudImage} />
      ),
      renderElement: () => null,
      disabled: true,
      tootTipText: 'Coming soon',
    },
    {
      label: 'Microsoft',
      key: 'azure',
      icon: (
        <img
          src={azureImage}
          alt="azure-image"
          className={classes.cloudImage}
        />
      ),
      renderElement: () => null,
      disabled: true,
      tootTipText: 'Coming soon',
    },
  ]

  const handleCloudTabChange = (index: number) => {
    setActiveTabIndex(index)
  }

  const handleCloseModal = () => {
    accountId && dispatch(searchRanking(accountId, selectedCloud))
    setOpenKeyword(false)
  }

  const initialValues: { keywordCompetitorMapping: KeyWordMap[] } = {
    keywordCompetitorMapping: keywordCompetitorList?.length
      ? [...keywordCompetitorList]
      : [{ keywords: [], competitors: [] }],
  }

  const content = [
    {
      content: isLoadingKeywordCompetitor ? (
        <LoadingLogo className={classes.loading} />
      ) : (
        <div className={classes.cloudContent}>
          <Formik
            initialValues={initialValues}
            validationSchema={KeywordCompetitorValidation}
            validateOnMount={true}
            onSubmit={() => {}}
            enableReinitialize
          >
            {formikProps => {
              return (
                <Form>
                  <FieldArray name="keywordCompetitorMapping">
                    {({ push, remove }) => {
                      return (
                        <AddEditKeywordsList
                          push={push}
                          formikProps={formikProps}
                          remove={remove}
                        />
                      )
                    }}
                  </FieldArray>
                </Form>
              )
            }}
          </Formik>
        </div>
      ),
    },
  ]

  return (
    <>
      <ModalConfirmV2
        title={title ? title : 'Keyword tracking'}
        icon={
          <FontAwesomeIcon style={{ height: '16px' }} icon={faRankingStar} />
        }
        description={
          <div className={classes.description}>
            Add keywords and competitors you want to track for each keyword.
          </div>
        }
        content={
          <>
            <div
              className={classes.mainTabContainer}
              data-testid="add-edit-keyword"
            >
              <MainTabsV2
                activeTabIndex={activeTabIndex}
                onChange={handleCloudTabChange}
                tabs={cloudTabs}
                actions={
                  <ButtonV2
                    data-testid={'manage-competitors'}
                    smallVariant
                    styleType="outlined"
                    className={classes.manageCompetitorBtn}
                    onClick={() => setOpenCompetitor(true)}
                  >
                    Manage competitors
                  </ButtonV2>
                }
              />
              {content[activeTabIndex].content}
            </div>
          </>
        }
        open={openKeyword}
        hideAcceptButton
        hideCancelButton
        onAccept={() => void 0}
        onDecline={handleCloseModal}
        variant={ModalSizeVariants.large}
      />
      {openCompetitor && (
        <AddEditCompetitors
          openCompetitor={openCompetitor}
          setOpenCompetitor={setOpenCompetitor}
        />
      )}
    </>
  )
}
